import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  TextField,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import { DateRangeOutlined } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  CouponType,
  CouponValidityType,
  ExecutionMethod,
} from '../../../store/models/campaignExperimental/CampaignFormDto';

interface CampaignTimingProps {
  executionMethod: ExecutionMethod;
  couponType: CouponType;
  startDate?: string;
  endDate?: string;
  fixedEndDate?: string;
  couponValidityType: CouponValidityType;
  validityDays?: number | null;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onFixedEndDateChange: (value: string) => void;
  onCouponValidityTypeChange: (value: CouponValidityType) => void;
  onValidityDaysChange: (value: number | null) => void;
  startDateError?: string;
  endDateError?: string;
  fixedEndDateError?: string;
  validityDaysError?: string;
  couponValidityTypeError?: string;
  disabled?: boolean;
}

const CampaignTiming: React.FC<CampaignTimingProps> = ({
  executionMethod,
  couponType,
  startDate,
  endDate,
  fixedEndDate,
  couponValidityType,
  validityDays,
  onStartDateChange,
  onEndDateChange,
  onFixedEndDateChange,
  onCouponValidityTypeChange,
  onValidityDaysChange,
  startDateError,
  endDateError,
  fixedEndDateError,
  validityDaysError,
  couponValidityTypeError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const today = React.useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const parseDate = (value?: string) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleDateChange = (value: Date | null, handler: (date: string) => void) => {
    if (value && !Number.isNaN(value.getTime())) {
      handler(value.toISOString());
    } else {
      handler('');
    }
  };

  const isScheduled = executionMethod === ExecutionMethod.SCHEDULED;
  const isEventDriven = executionMethod === ExecutionMethod.EVENT_DRIVEN;
  const isScheduledFree = isScheduled && couponType === CouponType.FREE;
  const isEventDrivenFree = isEventDriven && couponType === CouponType.FREE;
  const isScheduledPurchasable = isScheduled && couponType === CouponType.PURCHASABLE;
  const showValiditySection = isScheduledPurchasable || isEventDrivenFree;

  const prevCombination = React.useRef<{ executionMethod: ExecutionMethod; couponType: CouponType }>({
    executionMethod,
    couponType,
  });

  React.useEffect(() => {
    const prev = prevCombination.current;
    const combinationChanged =
      prev.executionMethod !== executionMethod || prev.couponType !== couponType;
    prevCombination.current = { executionMethod, couponType };

    if (!combinationChanged) {
      return;
    }

    if (
      couponValidityType === CouponValidityType.FIXED_END_DATE ||
      couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE
    ) {
      return;
    }

    if (isEventDrivenFree) {
      onCouponValidityTypeChange(CouponValidityType.DAYS_AFTER_PURCHASE);
    } else if (isScheduledPurchasable) {
      onCouponValidityTypeChange(CouponValidityType.FIXED_END_DATE);
      if (endDate) {
        onFixedEndDateChange(endDate);
      }
    } else if (isScheduledFree) {
      onCouponValidityTypeChange(CouponValidityType.DEFAULT_FROM_CAMPAIGN);
      onFixedEndDateChange('');
      onValidityDaysChange(null);
    }
  }, [
    couponValidityType,
    couponType,
    endDate,
    executionMethod,
    isEventDrivenFree,
    isScheduledFree,
    isScheduledPurchasable,
    onCouponValidityTypeChange,
    onFixedEndDateChange,
    onValidityDaysChange,
  ]);

  React.useEffect(() => {
    if (couponValidityType !== CouponValidityType.FIXED_END_DATE || !endDate) {
      return;
    }
    if (!fixedEndDate || new Date(fixedEndDate) < new Date(endDate)) {
      onFixedEndDateChange(endDate);
    }
  }, [couponValidityType, endDate, fixedEndDate, onFixedEndDateChange]);

  const [fixedDateValidationError, setFixedDateValidationError] = React.useState<string>('');

  React.useEffect(() => {
    if (
      couponValidityType !== CouponValidityType.FIXED_END_DATE ||
      !fixedEndDate ||
      !endDate
    ) {
      setFixedDateValidationError('');
      return;
    }

    if (new Date(fixedEndDate) < new Date(endDate)) {
      setFixedDateValidationError(t('campaignExperimental.create.fixedEndDateValidation'));
    } else {
      setFixedDateValidationError('');
    }
  }, [couponValidityType, endDate, fixedEndDate, t]);

  const combinedFixedEndError = fixedEndDateError || fixedDateValidationError;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DateRangeOutlined color="primary" />
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {t('campaignExperimental.create.campaignTimingTitle')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('campaignExperimental.create.campaignTimingDesc')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                label={t('campaignExperimental.create.startDateRequiredLabel')}
                value={parseDate(startDate)}
                minDate={today}
                onChange={(newValue) => handleDateChange(newValue, onStartDateChange)}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    disabled={disabled}
                    error={Boolean(startDateError)}
                    helperText={startDateError || t('campaignExperimental.create.startDateHelp')}
                    placeholder={t('campaignExperimental.create.startDatePlaceholder')}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                label={t('campaignExperimental.create.endDateRequiredLabel')}
                value={parseDate(endDate)}
                minDate={startDate ? parseDate(startDate) || today : today}
                onChange={(newValue) => handleDateChange(newValue, onEndDateChange)}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    disabled={disabled}
                    error={Boolean(endDateError)}
                    helperText={endDateError || t('campaignExperimental.create.endDateHelp')}
                    placeholder={t('campaignExperimental.create.endDatePlaceholder')}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        {isScheduledFree && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('campaignExperimental.create.scheduledFreeTimingHint')}
          </Typography>
        )}

        {showValiditySection && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2">
                {t('campaignExperimental.create.couponValidityLabel')}
              </Typography>
              {isScheduledPurchasable && (
                <Typography variant="caption" color="text.secondary">
                  {t('campaignExperimental.create.scheduledPurchasableValidityHint')}
                </Typography>
              )}
              {isEventDrivenFree && (
                <Typography variant="caption" color="text.secondary">
                  {t('campaignExperimental.create.eventDrivenValidityHint')}
                </Typography>
              )}
            </Box>
            <FormControl component="fieldset" fullWidth error={Boolean(couponValidityTypeError)} disabled={disabled}>
              <RadioGroup
                value={couponValidityType}
                onChange={(event) =>
                  onCouponValidityTypeChange(event.target.value as CouponValidityType)
                }
              >
                {isScheduledPurchasable && (
                  <Box sx={{ mb: 2, pl: 0.5 }}>
                    <FormControlLabel
                      value={CouponValidityType.FIXED_END_DATE}
                      control={<Radio />}
                      label={t('campaignExperimental.create.couponValidityFixedEndDate')}
                      disabled={disabled}
                    />
                    {couponValidityType === CouponValidityType.FIXED_END_DATE && (
                      <Box sx={{ pl: 4, pt: 1, maxWidth: 400 }}>
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={de}
                        >
                          <DatePicker
                            label={t('campaignExperimental.create.fixedEndDateLabel')}
                            value={parseDate(fixedEndDate)}
                            minDate={endDate ? parseDate(endDate) || today : today}
                            onChange={(newValue) => handleDateChange(newValue, onFixedEndDateChange)}
                            disabled={disabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                disabled={disabled}
                                error={Boolean(combinedFixedEndError)}
                                helperText={
                                  combinedFixedEndError ||
                                  t('campaignExperimental.create.fixedEndDateHelp')
                                }
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Box>
                    )}
                  </Box>
                )}

                {(isScheduledPurchasable || isEventDrivenFree) && (
                  <Box sx={{ mb: 1, pl: 0.5, maxWidth: 400 }}>
                    <FormControlLabel
                      value={CouponValidityType.DAYS_AFTER_PURCHASE}
                      control={<Radio />}
                      label={
                        isEventDrivenFree
                          ? t('campaignExperimental.create.validityDaysAssignmentLabel')
                          : t('campaignExperimental.create.validityDaysAfterPurchaseLabelScheduled')
                      }
                      disabled={disabled}
                    />
                    {couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE && (
                      <Box sx={{ pl: 4, pt: 1, maxWidth: 400 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            type="number"
                            label={t('campaignExperimental.create.validityDaysInputLabel')}
                            value={validityDays ?? ''}
                            placeholder={t('campaignExperimental.create.validityDaysPlaceholder')}
                            disabled={disabled}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (value === '') {
                                onValidityDaysChange(null);
                              } else {
                                onValidityDaysChange(Number(value));
                              }
                            }}
                            inputProps={{ min: 1, max: 365 }}
                            fullWidth
                            error={Boolean(validityDaysError)}
                            helperText={
                              validityDaysError ||
                              t('campaignExperimental.create.validityDaysHelper')
                            }
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('campaignExperimental.create.validityDaysUnitLabel')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </RadioGroup>
              {couponValidityTypeError && (
                <FormHelperText>{couponValidityTypeError}</FormHelperText>
              )}
            </FormControl>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CampaignTiming;
